import { validate as schemaValidator } from '@svs/nova-poolbetting/schema/utils';
import * as z from 'zod/v4';

import { couponIndexSchema } from '../coupon/entry.js';
import { dateSchema } from '../date-picker/entry.js';
import { drawNumberSchema } from '../draw/entry.js';
import { multiForecastOutcomeSchema } from '../forecast/entry.js';
import { couponProductEnum, couponProductSchema } from '../product/entry.js';
import { eventNumberSchema, sportType } from '../sport/entry.js';
import { externalWagerGuidSchema, wagerIdSchema } from '../wager/entry.js';
import { maskSchema } from './mask.js';

const couponProductQuerySchema = z.preprocess((value) => {
  if (typeof value === 'string' && /^\d+$/.test(value)) {
    return Number(value);
  }

  return value;
}, couponProductSchema);

export const modalTypeEnum = z.enum(['draw', 'stats', 'gameGuide']);
export type ModalTypes = z.infer<typeof modalTypeEnum>;

export const uploadErrorTypeEnum = z.enum([
  'UNAUTHORIZED',
  'CLIENT',
  'CONTENT_INVALID',
  'CONTENT_INVALID_HEADER',
  'CONTENT_BET_AMOUNT_MISSING',
  'CONTENT_DRAW_NUMBER_MISSING',
  'CONTENT_SYSTEM_SIZE_MISSING',
  'CONTENT_SYSTEM_UNSUPPORTED',
  'CONTENT_SYSTEM_MISSING',
  'CONTENT_SYSTEM_INCONSISTENT',
  'CONTENT_SYSTEM_SIZE_UNEXPECTED',
  'DRAW_CLOSED',
  'PRODUCT_NOT_SUPPORTED',
  'PRODUCT_MISMATCH',
  'AD_DRAW_MISMATCH',
  'INTERNAL',
  'UNKNOWN',
]);
export type UploadErrorType = z.infer<typeof uploadErrorTypeEnum>;

export const gameGuideTabEnum = z.enum(['spela', 'om', 'lottning']);
export type GameGuideTabs = z.infer<typeof gameGuideTabEnum>;

export const sheetTypeEnum = z.enum(['couponSettings', 'quickPick', 'wagerListSettings']).brand<'SheetType'>();

export const sheetTypeSchema = z
  .unknown()
  .refine((value): value is string => typeof value === 'string' && value.length > 0, {
    message: 'Expected a string with a value',
  })
  .pipe(sheetTypeEnum);

export type SheetTypes = z.infer<typeof sheetTypeSchema>;

export const COUPON_SETTINGS_SHEET = sheetTypeEnum.parse(sheetTypeEnum.enum.couponSettings);
export const QUICK_PICK_SHEET = sheetTypeEnum.parse(sheetTypeEnum.enum.quickPick);
export const WAGER_LIST_SETTINGS_SHEET = sheetTypeEnum.parse(sheetTypeEnum.enum.wagerListSettings);

/**
 * Zod enum for sorting by draw, the different options to sort a draw by
 */
export const sortDrawByTypeEnum = z.enum(['regCloseTime', 'netSale', 'extraMoney']);
export type SortDrawByType = z.infer<typeof sortDrawByTypeEnum>;

export const activatedDrawIdSchema = z.coerce.number().int().nonnegative().or(z.undefined()).brand<'ActivatedDrawId'>();

export const commissionPercentSchema = z.coerce.number().int().min(0).max(10).brand<'Commission'>();
export type Commission = z.infer<typeof commissionPercentSchema>;

/**
 * Marketplace specific filters that are used in browse game functionality for retail fractions
 * Notice that there is a loose connection to marketplace-filterTypes
 */
const marketplaceFilterSchema = z.object({
  searchName: z.string().optional().catch(undefined),
  products: z.array(couponProductEnum).optional().catch(undefined),
  fractionPriceMin: z.coerce.number().int().positive().optional().catch(undefined),
  fractionPriceMinDisplay: z.coerce.number().int().positive().optional().catch(undefined),
  fractionPriceMax: z.coerce.number().int().positive().optional().catch(undefined),
  fractionPriceMaxDisplay: z.coerce.number().int().positive().optional().catch(undefined),
  fractionCountMin: z.coerce.number().int().positive().optional().catch(undefined),
  fractionCountMinDisplay: z.coerce.number().int().positive().optional().catch(undefined),
  fractionCountMax: z.coerce.number().int().positive().optional().catch(undefined),
  fractionCountMaxDisplay: z.coerce.number().int().positive().optional().catch(undefined),
  systemPriceMin: z.coerce.number().int().positive().optional().catch(undefined),
  systemPriceMinDisplay: z.coerce.number().int().positive().optional().catch(undefined),
  systemPriceMax: z.coerce.number().int().positive().optional().catch(undefined),
  systemPriceMaxDisplay: z.coerce.number().int().positive().optional().catch(undefined),
});

/**
 * Query string schema, used by different MFE endpoints in tipsen products
 */
export const queryStringSchema = z.object({
  ...marketplaceFilterSchema.shape,
  draw: drawNumberSchema.optional().catch(undefined),
  drawOpen: z
    .string()
    .toLowerCase()
    .transform((val) => val !== 'false')
    .pipe(z.boolean())
    .optional()
    .catch(undefined),
  event: eventNumberSchema.optional().catch(undefined),
  product: couponProductQuerySchema.optional().catch(undefined),
  sortDrawsBy: sortDrawByTypeEnum.optional().catch(undefined),
  drawsBySport: z.array(sportType).or(sportType).optional().catch(undefined),
  modal: modalTypeEnum.optional().catch(undefined),
  tab: gameGuideTabEnum.optional().nullish().catch(undefined),
  sheet: sheetTypeSchema.optional().catch(undefined),
  coupon: couponIndexSchema.optional().catch(undefined),
  mask: maskSchema.optional().catch(undefined),
  bbr: z
    .preprocess((value) => value !== undefined, z.boolean())
    .optional()
    .catch(false),
  multiForecastOutcome: multiForecastOutcomeSchema.optional().catch(undefined),
  activatedDrawId: activatedDrawIdSchema.optional().catch(undefined),
  commission: commissionPercentSchema.optional().catch(undefined),
  error: uploadErrorTypeEnum.optional().catch(undefined),
  winPresentation: z
    .string()
    .toLowerCase()
    .transform((value) => value === 'true')
    .pipe(z.boolean())
    .optional()
    .catch(undefined),
});

/**
 * TS Query string type, used by different MFE endpoints in tipsen products
 */
export type QueryStringSchemaKeys = keyof z.infer<typeof queryStringSchema>;

export type QueryStringSchema = z.infer<typeof queryStringSchema>;

export type Validator = {
  query: z.infer<typeof queryStringSchema>;
};

export const validate = {
  query: schemaValidator(queryStringSchema),
  path: schemaValidator(
    z.object({
      wagerId: wagerIdSchema.optional(),
      externalWagerGuid: externalWagerGuidSchema.optional(),
      date: dateSchema.optional(),
    })
  ),
} as const;
